import { ColumnsType } from 'antd/es/table';
import {
  CalculationPickingResponse,
  PapProgressStatus,
  RoutesEvaluationRatesResponse,
  RoutesExecutionSummaryResponse,
  StorePickingInsightsResponse,
} from '@ydistri/api-sdk';
import { OutlierValue, YColumnsType } from '@ydistri/ds';
import { formatNumber, formatTimeDuration } from '@ydistri/generic-utils';
import {
  EXPORT_NICE_TIME_FORMAT,
  secondsToDayFractionForExport,
} from '../../../components/global/TableExport/tableExportLib';
import { computeRemSize } from '@ydistri/theme';

export enum PickingResultsPart {
  EXECUTION = 'execution',
  PERFORMANCE = 'performance',
  ISSUES = 'issues',
}

export type PickingResultsExtended = Partial<RoutesExecutionSummaryResponse> &
  Partial<RoutesEvaluationRatesResponse> & {
    executedQuantityPercentage: number;
    executedValuePercentage: number;
    executedSkuCountPercentage: number;
  };

export type CalculationPickingExtendedResponse = Pick<
  CalculationPickingResponse,
  'calculationCreatedAt' | 'pickingStatus' | 'finishExecutionUntil'
> & {
  pickingResults: PickingResultsExtended;
};

const statusMap: Record<PapProgressStatus, string> = {
  [PapProgressStatus.NotStarted]: 'Not started',
  [PapProgressStatus.PickingStarted]: 'Started',
  [PapProgressStatus.PickingDone]: 'Picking completed',
  [PapProgressStatus.Finalized]: 'Finalized',
};

export const translatePapProgressStatus = (status: PapProgressStatus): string => {
  return statusMap[status] || 'Unknown status';
};

export const getPickInsightsColumns = (
  storeColumns: YColumnsType<StorePickingInsightsResponse>[],
): ColumnsType<StorePickingInsightsResponse> => {
  const columns: YColumnsType<StorePickingInsightsResponse>[] = [...storeColumns];

  columns.push(
    {
      title: 'Executed Quantity',
      dataIndex: 'executedQuantity',
      key: 'executedQuantity',
      width: computeRemSize(160),
      align: 'right',
      sorter: (a, b) => a.executedQuantity - b.executedQuantity,
    },
    {
      title: 'Executed Value',
      dataIndex: 'executedValue',
      key: 'executedValue',
      width: computeRemSize(160),
      align: 'right',
      render: (value: number) => formatNumber(value),
      sorter: (a, b) => a.executedValue - b.executedValue,
    },
    {
      title: 'Picking Positions',
      dataIndex: 'pickingPositionCount',
      key: 'pickingPositionCount',
      width: computeRemSize(100),
      align: 'right',
      sorter: (a, b) => a.pickingPositionCount - b.pickingPositionCount,
    },
    {
      title: 'Total Picking Time',
      dataIndex: 'totalPickingTime',
      key: 'totalPickingTime',
      width: computeRemSize(120),
      align: 'right',
      render: (value: number, record) => {
        const renderedValue = formatTimeDuration(value, ['hours', 'minutes']);
        if (value !== record.trimmedPickingTime) {
          return (
            <OutlierValue
              value={renderedValue}
              tooltip="Some of the pickings took more than 20 minutes and were removed in the trimmed column"
            />
          );
        } else {
          return renderedValue;
        }
      },
      exportRender: (value: number) => secondsToDayFractionForExport(value), // when exporting to excel, export number of seconds (for easy excel sorting)
      exportFormat: EXPORT_NICE_TIME_FORMAT,
      sorter: (a, b) => a.totalPickingTime - b.totalPickingTime,
    },
    {
      title: 'Total Picking Time (trimmed)',
      dataIndex: 'trimmedPickingTime',
      key: 'trimmedPickingTime',
      width: computeRemSize(160),
      align: 'right',
      render: (value: number) => formatTimeDuration(value, ['hours', 'minutes']),
      exportRender: (value: number) => secondsToDayFractionForExport(value), // when exporting to excel, export number of seconds (for easy excel sorting)
      exportFormat: EXPORT_NICE_TIME_FORMAT,
      sorter: (a, b) => a.trimmedPickingTime - b.trimmedPickingTime,
    },
    {
      title: 'Average Picking Time (trimmed)',
      dataIndex: 'trimmedMeanPickingTime',
      key: 'trimmedMeanPickingTimePerSku',
      width: computeRemSize(230),
      align: 'right',
      render: (value: number) => formatTimeDuration(value),
      exportRender: (value: number) => secondsToDayFractionForExport(value), // when exporting to excel, export number of seconds (for easy excel sorting)
      exportFormat: EXPORT_NICE_TIME_FORMAT,
      sorter: (a, b) => a.trimmedMeanPickingTime - b.trimmedMeanPickingTime,
    },
    {
      title: 'Median Picking Time (trimmed)',
      dataIndex: 'trimmedMedianPickingTime',
      key: 'trimmedMedianPickingTime',
      width: computeRemSize(230),
      align: 'right',
      render: (value: number) => formatTimeDuration(value),
      exportRender: (value: number) => secondsToDayFractionForExport(value), // when exporting to excel, export number of seconds (for easy excel sorting)
      exportFormat: EXPORT_NICE_TIME_FORMAT,
      sorter: (a, b) => a.trimmedMedianPickingTime - b.trimmedMedianPickingTime,
    },
  );

  return columns;
};
